declare interface ISlice {
    x: number;
    y: number;
    w: number;
    h: number;
}

type Tool = 'mark' | 'remove' | 'grid';
type Imagelike = HTMLCanvasElement | HTMLImageElement;

//-------------------

const glsl = e => e;

import {Renderer} from './requires/OSGL';

const firstPassRenderer = new Renderer('' + glsl`
    vec4 v = texture2D(u_image, v_texCoord);
    int kernelSize = (halfSize*2 + 1)*(halfSize*2 + 1);
    vec4 meanColor = vec4(0.0, 0.0, 0.0, 0.0);
    if (halfSize > 0) {
        for (int y = -8; y <= 8; y++) {
            if (y < -halfSize) {
                continue;
            }
            if (y > halfSize) {
                break;
            }
            for (int x = -8; x <= 8; x++) {
                if (x < -halfSize) {
                    continue;
                }
                if (x > halfSize) {
                    break;
                }
                vec4 v1 = texture2D(u_image, v_texCoord + vec2(x, y) * pixel);
                vec4 v2 = texture2D(u_image, v_texCoord + vec2(-x, -y) * pixel);
                vec4 d1 = abs(v - v1);
                vec4 d2 = abs(v - v2);
                vec4 rv1 = vec4(((d1[0] < d2[0]) ? v1[0] : v2[0]),
                                ((d1[1] < d2[1]) ? v1[1] : v2[1]),
                                ((d1[2] < d2[2]) ? v1[2] : v2[2]), 1);
                meanColor += rv1;
            }
        }
        gl_FragColor = meanColor / float(kernelSize);
    } else {
        gl_FragColor = texture2D(u_image, v_texCoord);
    }
    gl_FragColor.r = (gl_FragColor.r - 0.5) * contrast + 0.5 + brightness;
    gl_FragColor.g = (gl_FragColor.g - 0.5) * contrast + 0.5 + brightness;
    gl_FragColor.b = (gl_FragColor.b - 0.5) * contrast + 0.5 + brightness;
    float l = min(gl_FragColor.r, min(gl_FragColor.g, gl_FragColor.b));
    gl_FragColor.a = l < bgThreshold ? 1.0 : 0.0;
`, {
    brightness: 'float',
    contrast: 'float',
    bgThreshold: 'float',
    halfSize: 'int'
});

//--------------------------------------

const secondPassRenderer = new Renderer('' + glsl`
    vec4 v = texture2D(u_image, v_texCoord);
    int kernelSize = (halfSize*2 + 1)*(halfSize*2 + 1);
    float minA = 1.0;
    for (int y = 0; y <= 8; y++) {
        if (y > halfSize) {
            break;
        }
        for (int x = 0; x <= 8; x++) {
            if (x > halfSize) {
                break;
            }
            vec4 v1 = texture2D(u_image, v_texCoord + vec2(x, y) * pixel);
            vec4 v2 = texture2D(u_image, v_texCoord + vec2(x, -y) * pixel);
            vec4 v3 = texture2D(u_image, v_texCoord + vec2(-x, y) * pixel);
            vec4 v4 = texture2D(u_image, v_texCoord + vec2(-x, -y) * pixel);
            minA = min(minA, min(v1.a, min(v2.a, min(v3.a, v4.a))));
        }
    }
    gl_FragColor = vec4(v.r, v.g, v.b, minA);
`, {
    halfSize: 'int'
});

//--------------------------------------

const antialiasPassRenderer = new Renderer('' + glsl`
    float alpha;
    float aNW = texture2D(u_image, (v_texCoord + vec2(-1.0, -1.0) * pixel)).a;
    float aNE = texture2D(u_image, (v_texCoord + vec2(1.0, -1.0) * pixel)).a;
    float aSW = texture2D(u_image, (v_texCoord + vec2(-1.0, 1.0) * pixel)).a;
    float aSE = texture2D(u_image, (v_texCoord + vec2(1.0, 1.0) * pixel)).a;
    float aM  = texture2D(u_image, v_texCoord).a;
    float aMin = min(aM, min(min(aNW, aNE), min(aSW, aSE)));
    float aMax = max(aM, max(max(aNW, aNE), max(aSW, aSE)));

    vec2 dir;
    dir.x = -((aNW + aNE) - (aSW + aSE));
    dir.y =  ((aNW + aSW) - (aNE + aSE));

    float dirReduce = max((aNW + aNE + aSW + aSE) *
                        (0.25 * FXAA_REDUCE_MUL), FXAA_REDUCE_MIN);

    float rcpDirMin = 1.0 / (min(abs(dir.x), abs(dir.y)) + dirReduce);
    dir = min(vec2(FXAA_SPAN_MAX, FXAA_SPAN_MAX),
            max(vec2(-FXAA_SPAN_MAX, -FXAA_SPAN_MAX),
            dir * rcpDirMin)) * pixel;
    float aA = 0.5 * (
        texture2D(u_image, v_texCoord + dir * (1.0 / 3.0 - 0.5)).a +
        texture2D(u_image, v_texCoord + dir * (2.0 / 3.0 - 0.5)).a);
    float aB = aA * 0.5 + 0.25 * (
        texture2D(u_image, v_texCoord + dir * -0.5).a +
        texture2D(u_image, v_texCoord + dir * 0.5).a);
    if ((aB < aMin) || (aB > aMax))
        alpha = aA;
    else
        alpha = aB;
    vec4 v = texture2D(u_image, v_texCoord);
    gl_FragColor = vec4(v.r, v.g, v.b, alpha);
`, {}, glsl`
    #define FXAA_REDUCE_MIN   (1.0 / 128.0)
    #define FXAA_REDUCE_MUL   (1.0 / 8.0)
    #define FXAA_SPAN_MAX     8.0
`);

//--------------------------------------

const img = document.createElement('img');
const slices: Array<ISlice> = [];
let w: number, h: number;
let tool: Tool = 'mark';
let currentSlice: ISlice;

const form = document.getElementById("theForm") as HTMLFormElement,
    getValue = function(t) {
        const e = form.elements.namedItem(t) as HTMLInputElement;
        if (e) return -1 !== ["number", "range"].indexOf(e.type) ? Number(e.value) : -1 !== ["radio", "checkbox"].indexOf(e.type) ? e.checked : e.value;
        throw new Error("No such named element: " + t);
    },
    setValue = function(t, e) {
        const o = form.elements.namedItem(t) as HTMLInputElement;
        if (!o) throw new Error("No such named element: " + t);
        o.value = e;
    };

const input = document.getElementById('theInputFile') as HTMLInputElement,
      scaledImage = document.createElement('canvas'),
      scaledImageX = scaledImage.getContext('2d'),
      canvas = document.createElement('canvas'),
      x = canvas.getContext('2d'),
      canvasUi = document.getElementById('theWorkingCanvas') as HTMLCanvasElement,
      xui = canvasUi.getContext('2d');

input.addEventListener('change', e => {
    const {files} = e.target as any;
    const fr = new FileReader();
    fr.onload = function () {
        img.src = fr.result as string;
    }
    fr.readAsDataURL(files[0]);
});

const refreshImage = async() => {
    if (!img.src) {
        return;
    }
    const threshold = getValue('threshold') as number,
          denoise = getValue('denoise') as number,
          contractRadius = getValue('contract') as number,
          scale = 1 - (getValue('scale') as number);

    const targetSizeX = Math.round(img.width * scale),
          targetSizeY = Math.round(img.height * scale);
    if (canvas.width !== targetSizeX || canvas.height !== targetSizeY) {
        scaledImage.width = canvas.width = w = targetSizeX;
        scaledImage.height = canvas.height = h = targetSizeY;
        scaledImageX.clearRect(0, 0, w, h);
        scaledImageX.drawImage(img, 0, 0, img.width, img.height, 0, 0, w, h);
    }
    let product: Imagelike = scaledImage;

    product = firstPassRenderer.render(product, {
        brightness: getValue('brightness'),
        contrast: getValue('contrast'),
        bgThreshold: threshold / 256,
        halfSize: denoise
    });
    if (contractRadius > 0) {
        product = secondPassRenderer.render(product, {
            halfSize: contractRadius
        });
    }
    product = antialiasPassRenderer.render(product);
    x.clearRect(0, 0, w, h);
    x.drawImage(product, 0, 0);

    refreshUi();
}

const refreshUi = function () {
    if (canvasUi.width !== canvas.width || canvasUi.height !== canvas.height) {
        canvasUi.width = w = canvas.width;
        canvasUi.height = h = canvas.height;
    }
    xui.clearRect(0, 0, w, h);
    xui.drawImage(canvas, 0, 0);

    xui.strokeStyle = '#f42e1f';
    xui.lineWidth = 2;
    if (xui.setLineDash) {
        xui.setLineDash([5, 5]);
    }
    for (const slice of slices) {
        xui.strokeRect(slice.x, slice.y, slice.w, slice.h);
    }
}

img.addEventListener('load', () => {
    slices.length = 0;
    refreshImage();
});

const onCanvasPress = e => {
    if (tool === 'mark') {
        currentSlice = {
            x: e.offsetX,
            y: e.offsetY,
            w: 0,
            h: 0
        };
        slices.push(currentSlice);
    } else if (tool === 'remove') {
        for (const slice of slices) {
            if (slice.x < e.offsetX &&
                slice.y < e.offsetY &&
                slice.x + slice.w > e.offsetX &&
                slice.y + slice.h > e.offsetY
            ) {
                slices.splice(slices.indexOf(slice), 1);
                break;
            }
        }
    }
}
const onCanvasMove = e => {
    if (tool === 'mark') {
        if (!currentSlice) {
            return;
        }
        currentSlice.w = e.offsetX - currentSlice.x;
        currentSlice.h = e.offsetY - currentSlice.y;
        refreshUi();
    }
};
const onCanvasRelease = e => {
    if (tool === 'mark') {
        currentSlice.w = e.offsetX - currentSlice.x;
        currentSlice.h = e.offsetY - currentSlice.y;
        if (currentSlice.w < 0) {
            currentSlice.x += currentSlice.w;
            currentSlice.w *= -1;
        }
        if (currentSlice.h < 0) {
            currentSlice.y += currentSlice.h;
            currentSlice.h *= -1;
        }
        if (currentSlice.w < 2 || currentSlice.h < 2) {
            slices.splice(slices.indexOf(currentSlice), 1);
        }
    }
    currentSlice = void 0;
    refreshUi();
};

canvasUi.addEventListener('pointerdown', onCanvasPress);
canvasUi.addEventListener('pointermove', onCanvasMove);
canvasUi.addEventListener('pointerup', onCanvasRelease);

form.addEventListener('submit', e => e.preventDefault());

for (const name of ['threshold', 'denoise', 'contract', 'scale', 'brightness', 'contrast']) {
    (form.elements.namedItem(name) as HTMLInputElement).addEventListener('input', refreshImage);
}

const toolboxButtons = document.querySelectorAll('.aToolboxButton') as NodeListOf<HTMLButtonElement>;
for (const button of toolboxButtons) {
    button.addEventListener('click', e => {
        for (const button2 of toolboxButtons) {
            button2.classList.remove('active');
        }
        button.classList.add('active');
        tool = button.value as Tool;
        e.preventDefault();
    });
}

const isArrayTransparent = (array: Uint8ClampedArray) => {
    for (let i = 0; i < array.length; i+= 4) {
        if (array[i + 3] !== 0) {
            return false;
        }
    }
    return true;
}
const getEmptyEdges = (context: CanvasRenderingContext2D, slice: ISlice) => {
    const top    = context.getImageData(slice.x, slice.y, slice.w, 1).data,
          right  = context.getImageData(slice.x + slice.w - 1, slice.y, 1, slice.h).data,
          bottom = context.getImageData(slice.x, slice.y + slice.h - 1, slice.w, 1).data,
          left   = context.getImageData(slice.x, slice.y, 1, slice.h).data;
    return [
        isArrayTransparent(top),
        isArrayTransparent(right),
        isArrayTransparent(bottom),
        isArrayTransparent(left)
    ];
};
const trimTransparency = (context: CanvasRenderingContext2D, startingSlice: ISlice) => {
    const slice: ISlice = {...startingSlice};
    while (slice.w > 1 && slice.h > 1) {
        const e = getEmptyEdges(context, slice);
        // Is there at least one transparent side?
        if (!(e[0] || e[1] || e[2] || e[3])) {
            return slice;
        }
        // Cut away what is transparent
        if (e[0]) {
            slice.y ++;
            slice.h --;
        }
        if (e[1]) {
            slice.w --;
        }
        if (e[2]) {
            slice.h --;
        }
        if (e[3]) {
            slice.x ++;
            slice.w --;
        }
    }
    if (slice.w <= 1 || slice.h <= 1) {
        return false;
    }
    return slice;
};

const downloadDataURI = function (data: string, name: string) {
    const a = document.createElement('a');
    a.setAttribute('href', data);
    a.setAttribute('download', name);
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};

import * as zip from '@zip.js/zip.js';
const downloadArchive = async function (canvases: Array<HTMLCanvasElement>, name: string) {
    // use a BlobWriter to store with a ZipWriter the zip into a Blob object
    const blobWriter = new zip.BlobWriter('application/zip');
    const writer = new zip.ZipWriter(blobWriter);

    // - create a Data64URIWriter object to write the zipped data into a data URI
    // - create a ZipWriter object with the Data64URIWriter object as parameter
    const zipWriter = new zip.ZipWriter(new zip.Data64URIWriter('application/zip'));

    for (let i = 0; i < canvases.length; i++) {
        const name = `${i+1}.png`,
              c = canvases[i];
        await zipWriter.add(name, new zip.Data64URIReader(c.toDataURL()));
    }

    downloadDataURI(await zipWriter.close(), name);
};
const exportSprites = () => {
    if (!slices.length) {
        alert('You should first mark sprites with a "Mark sprites" tool on the left.');
        return;
    }
    const trimmed = slices
        .map(slice => trimTransparency(x, slice))
        .filter(slice => slice) as Array<ISlice>; // Remove invalid (transparent) slices. Also tell TS that falsey values were removed
    if (trimmed.length === 1) {
        // Open as is
        const [slice] = trimmed; //
        const exportCanvas = document.createElement('canvas'),
              ex = exportCanvas.getContext('2d');
        exportCanvas.width = slice.w;
        exportCanvas.height = slice.h;
        ex.drawImage(canvas, slice.x, slice.y, slice.w, slice.h, 0, 0, slice.w, slice.h);
        const now = new Date();
        const dataURI = exportCanvas.toDataURL('image/png');
        downloadDataURI(dataURI, `Comigo's Paper Sprite-o'matic ${now.toLocaleString()}.png`);
    } else {
        const exportCanvases = trimmed.map((slice): HTMLCanvasElement => {
            const c = document.createElement('canvas'),
                  x = c.getContext('2d');
            c.width = slice.w;
            c.height = slice.h;
            x.drawImage(canvas, slice.x, slice.y, slice.w, slice.h, 0, 0, slice.w, slice.h);
            return c;
        });
        const now = new Date();
        downloadArchive(exportCanvases, `Comigo's Paper Sprite-o'matic ${now.toLocaleString()}.zip`);
    }
};

(document.getElementById('theDownloadButton') as HTMLButtonElement).addEventListener('click', e => {
    exportSprites();
    e.preventDefault();
});

const overlay = document.getElementById('theGridOverlay'),
      workspace = document.getElementById('theWorkspace');
const updateOverlay = () => {
    const w = getValue('gridX') as number,
          h = getValue('gridY') as number;
    overlay.style.backgroundImage = `linear-gradient(to right, transparent 0%, transparent ${100 - (100 / w)}%, #f42e1f55 100%), linear-gradient(to bottom, transparent 0%, transparent ${100 - (100 / h)}%, #f42e1f55 100%)`;
    overlay.style.backgroundSize = `${w}px ${h}px`;
}
let draggingTheGrid = false;
workspace.addEventListener('pointerdown', () => {
    if (tool === 'grid') {
        draggingTheGrid = true;
    }
});
workspace.addEventListener('pointermove', e => {
    if (!draggingTheGrid) return;
    overlay.style.backgroundPosition = `${e.offsetX}px ${e.offsetY}px, ${e.offsetX}px ${e.offsetY}px`;
});
workspace.addEventListener('pointerleave', () => {
    draggingTheGrid = false;
});
workspace.addEventListener('pointerup', () => {
    draggingTheGrid = false;
});

document.querySelectorAll('[name="gridX"], [name="gridY"]').forEach(e => {
    e.addEventListener('input', updateOverlay);
});
updateOverlay();


//--------------------------------------


/* from https://github.com/rvagg/delayed/blob/master/delayed.js */
const cumulativeDelayed = (function () {
    var slice = function (arr, i?) {
        return Array.prototype.slice.call(arr, i);
    };
    /*
        Returns a new function that will delay execution
        of the original function for the specified number
        of milliseconds when called. Execution will be further
        delayed for the same number of milliseconds upon each
        subsequent call before execution occurs.

        The best way to explain this is to show its most obvious
        use-case: keyboard events in the browser.
    */
    function cumulativeDelayed (fn, ms, ctx?) {
        var args = slice(arguments, 3),
            timeout = null;
        return function cumulativeDelayeder () {
            var _args = slice(arguments),
                f = function cumulativeDelayedCaller () {
                    return fn.apply(ctx || null, args.concat(_args));
                };
            if (timeout != null) {
                clearTimeout(timeout);
            }
            return timeout = setTimeout(f, ms);
        };
    }
    return cumulativeDelayed;
})();

//--------------------------------------

const writeToLS = cumulativeDelayed(function () {
    const data = Object
        .values(form)
        .filter(field => !['', 'input'].includes(field.name))
        .reduce((obj: object, field) => {
            obj[field.name] = field.value;
            return obj
        }, {});
    localStorage.COMIGOPaperSpritomatic = JSON.stringify(data);
}, 500);
form.addEventListener('change', () => {
    writeToLS();
});

if (localStorage.COMIGOPaperSpritomatic) {
    const data = JSON.parse(localStorage.COMIGOPaperSpritomatic);
    for (const field in data) {
        setValue(field, data[field]);
    }
}